import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CL_REGIONS, CL_REGION_NAMES } from "@/lib/clRegions";
import { useFavCommunes } from "@/lib/favCommunes";
import { MapPin, ChevronDown, Check, LocateFixed, Loader2, Map, Star } from "lucide-react";

const FavStar = ({ on, onToggle, name }) => (
  <button type="button" aria-label={on ? `Quitar ${name} de favoritas` : `Guardar ${name} en favoritas`}
    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
    data-testid={`fav-toggle-${name}`}
    className="grid place-items-center w-6 h-6 -mr-1 rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
    <Star className={`w-3.5 h-3.5 transition-transform ${on ? "fill-amber-400 text-amber-400 scale-110" : "text-muted-foreground/60"}`} />
  </button>
);

const fmtCount = (n) => (n == null ? null : n === 0 ? "Sin talleres" : `${n} taller${n === 1 ? "" : "es"}`);

const Item = ({ label, value, active, onSelect, testId, icon, hint, muted, fav, sub }) => (
  <CommandItem value={value || label} onSelect={onSelect} data-testid={testId} className="gap-2 py-1.5">
    {icon || <Check className={`w-4 h-4 text-primary ${active ? "opacity-100" : "opacity-0"}`} />}
    <span className="flex-1 min-w-0 leading-tight">
      <span className={`block truncate ${active ? "font-semibold text-primary" : ""}`}>{label}</span>
      {sub && <span className={`block text-[11px] ${sub === "Sin talleres" ? "text-muted-foreground/70" : "text-muted-foreground"}`} data-testid={`${testId}-count`}>{sub}</span>}
    </span>
    {hint && <span className={`text-[10px] font-semibold ${muted ? "text-muted-foreground tabular-nums" : "uppercase tracking-wide text-primary/80"}`}>{hint}</span>}
    {fav && <FavStar on={fav.on} onToggle={fav.toggle} name={label} />}
  </CommandItem>
);

const NEAR = 6;

function useSortedCommunes(region, ref) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    if (!region || region === "all") return;
    api.get("/geo/communes", { params: { region, ...(ref ? { ref } : {}) } })
      .then((r) => setRows(r.data))
      .catch(() => setRows((CL_REGIONS[region] || []).map((c) => ({ commune: c, km: null }))));
  }, [region, ref]);
  return rows;
}

function CommuneList({ region, commune, geo, onPick, onPickPlace }) {
  const { favs, isFav, toggle } = useFavCommunes();
  const fav = (c) => ({ on: isFav(c), toggle: () => toggle(region, c) });
  const ref = geo?.region === region ? geo.commune : commune !== "all" ? commune : null;
  const rows = useSortedCommunes(region, ref);
  const [search, setSearch] = useState("");
  const [more, setMore] = useState(false);
  const sorted = !!rows?.some((r) => r.km != null);
  const cnt = (c) => fmtCount(rows?.find((r) => r.commune === c)?.count);
  const list = (rows || []).filter((r) => r.commune !== geo?.commune && r.commune !== ref);
  const shown = search || more || !sorted ? list : list.slice(0, NEAR);
  const refItem = ref && !geo?.commune ? ref : null;
  return (
    <Command>
      <CommandInput placeholder="Buscar comuna…" value={search} onValueChange={setSearch} data-testid="location-search" />
      <CommandList className="max-h-80">
        <CommandEmpty>Sin resultados</CommandEmpty>
        {favs.length > 0 && (
          <CommandGroup heading="Mis favoritas" data-testid="location-favs">
            {favs.map((f) => (
              <Item key={f.commune} label={f.commune} value={`${f.commune} favorita`} active={commune === f.commune}
                onSelect={() => onPickPlace(f.region, f.commune)} testId={`location-fav-${f.commune}`}
                hint={f.region !== region ? f.region : null} muted
                icon={<Star className="w-4 h-4 fill-amber-400 text-amber-400" />} fav={{ on: true, toggle: () => toggle(f.region, f.commune) }} />
            ))}
          </CommandGroup>
        )}
        {geo?.region === region && (
          <CommandGroup heading="Tu ubicación">
            <Item label={geo.commune} active={commune === geo.commune} onSelect={() => onPick(geo.commune)} testId="location-opt-here" hint="Estás aquí" sub={cnt(geo.commune)}
              icon={<LocateFixed className="w-4 h-4 text-primary" />} fav={fav(geo.commune)} />
          </CommandGroup>
        )}
        {refItem && (
          <CommandGroup heading="Comuna elegida">
            <Item label={refItem} active onSelect={() => onPick(refItem)} testId="location-opt-current" sub={cnt(refItem)} fav={fav(refItem)} />
          </CommandGroup>
        )}
        <CommandGroup heading={sorted && !search ? `Comunas cercanas · ${region}` : `Comunas de ${region}`}>
          {rows === null ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Ordenando por cercanía…</div>
          ) : shown.map((r) => (
            <Item key={r.commune} label={r.commune} active={commune === r.commune} onSelect={() => onPick(r.commune)}
              testId={`location-opt-${r.commune}`} sub={fmtCount(r.count)} fav={fav(r.commune)} />
          ))}
        </CommandGroup>
        {sorted && !search && !more && list.length > NEAR && (
          <button type="button" onClick={() => setMore(true)} data-testid="location-more"
            className="mx-2 mb-1 flex w-[calc(100%-1rem)] items-center justify-center gap-1 rounded-lg py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors">
            Ver más comunas ({list.length - NEAR})<ChevronDown className="w-4 h-4" />
          </button>
        )}
        <CommandSeparator />
        <CommandGroup>
          <Item label="Toda la región" active={commune === "all"} onSelect={() => onPick("all")} testId="location-opt-region" icon={<Map className="w-4 h-4 text-muted-foreground" />} />
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function LocatedChip({ region, commune, geo, setRegion, setCommune, onClear }) {
  const [open, setOpen] = useState(false);
  const here = geo && geo.commune === commune;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="location-chip"
          className="group inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 pl-2 pr-3 py-1.5 text-sm transition-colors hover:bg-primary/10 hover:border-primary/50 animate-in fade-in zoom-in-95 duration-300">
          <span className="relative grid place-items-center w-6 h-6 rounded-full bg-primary text-primary-foreground">
            {here && <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />}
            <MapPin className="relative w-3.5 h-3.5" />
          </span>
          <span className="flex flex-col items-start leading-tight text-left">
            <span className="font-semibold text-foreground max-w-[150px] truncate" data-testid="location-chip-commune">{commune === "all" ? region : commune}</span>
            <span className="text-[10px] text-muted-foreground">{commune === "all" ? "Toda la región" : here ? "Tu ubicación" : region}</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-primary transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0 overflow-hidden rounded-xl" data-testid="location-popover">
        <CommuneList region={region} commune={commune} geo={geo} onPick={(c) => { setCommune(c); setOpen(false); }}
          onPickPlace={(r, c) => { setRegion(r); setCommune(c); setOpen(false); }} />
        <div className="border-t border-border p-2">
          <button type="button" onClick={() => { setOpen(false); onClear(); }} data-testid="location-clear"
            className="group flex w-full items-center justify-center gap-2 rounded-full border border-primary/60 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/15 hover:border-primary active:scale-[0.98]">
            Elegir ubicación<MapPin className="w-4 h-4 transition-transform group-hover:scale-110" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ManualPickers({ region, setRegion, commune, setCommune, onLocate, locating }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-2 w-full sm:w-auto order-last sm:order-none animate-in fade-in slide-in-from-left-2 duration-300" data-testid="location-manual">
      <Select value={region} onValueChange={(v) => { setRegion(v); setCommune("all"); }}>
        <SelectTrigger className="h-9 flex-1 min-w-0 sm:flex-none sm:w-[190px] rounded-full" data-testid="region-filter"><span className="truncate">{region === "all" ? "Región" : region}</span></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas las regiones</SelectItem>
          {CL_REGION_NAMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
        </SelectContent>
      </Select>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={region === "all"} className="h-9 flex-1 min-w-0 sm:flex-none sm:w-[180px] justify-between rounded-full font-normal" data-testid="commune-filter">
            <span className="flex items-center gap-1.5 truncate"><MapPin className="w-4 h-4 text-muted-foreground shrink-0" />{commune === "all" ? "Comuna" : commune}</span>
            <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[280px] p-0 rounded-xl">
          <CommuneList region={region} commune={commune} onPick={(c) => { setCommune(c); setOpen(false); }}
            onPickPlace={(r, c) => { setRegion(r); setCommune(c); setOpen(false); }} />
        </PopoverContent>
      </Popover>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={onLocate} disabled={locating} aria-label="Usar mi ubicación" data-testid="location-locate"
              className="grid place-items-center w-9 h-9 shrink-0 rounded-full text-primary hover:bg-primary/10 transition-colors disabled:opacity-50">
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-[18px] h-[18px]" />}
            </button>
          </TooltipTrigger>
          <TooltipContent>Usar mi ubicación</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function FavPills({ commune, setRegion, setCommune }) {
  const { favs } = useFavCommunes();
  const others = favs.filter((f) => f.commune !== commune);
  if (!others.length) return null;
  return (
    <div className="order-last sm:order-none w-full sm:w-auto flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="fav-pills">
      {others.map((f) => (
        <button key={f.commune} type="button" onClick={() => { setRegion(f.region); setCommune(f.commune); }} data-testid={`fav-pill-${f.commune}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary animate-in fade-in zoom-in-95 duration-300">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />{f.commune}
        </button>
      ))}
    </div>
  );
}

export function ExploreLocation({ status, geo, region, setRegion, commune, setCommune, onClear, onLocate }) {
  if (status === "locating") {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-2 text-sm text-muted-foreground" data-testid="location-locating">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />Detectando tu comuna…
      </span>
    );
  }
  const pills = <FavPills commune={commune} setRegion={setRegion} setCommune={setCommune} />;
  if (status === "located" && region !== "all") {
    return <><LocatedChip region={region} commune={commune} geo={geo} setRegion={setRegion} setCommune={setCommune} onClear={onClear} />{pills}</>;
  }
  return <><ManualPickers region={region} setRegion={setRegion} commune={commune} setCommune={setCommune} onLocate={onLocate} locating={false} />{pills}</>;
}
